import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './UserDetailsForm.css';
import { useLanguage } from './LanguageContext';
import { PRICING_CONFIG, PLAN_TYPES } from '../config/pricing';

interface UserDetailsFormProps {
  email: string;
  onComplete?: () => void;
  onSubmitOnboarding: (formData: any) => Promise<{ success: boolean; customerId?: number; message?: string }>;
}

const subsectorOptions = [
  { label: 'Restaurant', value: 'Restaurant' },
  { label: 'Bar', value: 'Bar' },
  { label: 'Food Truck', value: 'Food Truck' },
];

const getSteps = (t: (key: keyof typeof import('../locales/en-US.json')) => string) => [
  t('companyInfo'),
  t('address'),
  t('taxMerchantInfo'),
  t('deviceConfiguration'),
];

const LOCAL_STORAGE_KEY = 'izypostOnboardingForm';


const UserDetailsForm: React.FC<UserDetailsFormProps> = ({ email, onComplete, onSubmitOnboarding }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  // Restore from localStorage if present
  const getInitialState = () => {
    if (typeof window === 'undefined') return {};
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Always use the latest email prop
        return { ...parsed, email };
      }
    } catch {}
    return { email };
  };

  const [company, setCompany] = useState(() => getInitialState().company || '');
  const [companyTel, setCompanyTel] = useState(() => getInitialState().companyTel || '');
  const [streetNumber, setStreetNumber] = useState(() => getInitialState().streetNumber || '');
  const [streetName, setStreetName] = useState(() => getInitialState().streetName || '');
  const [city, setCity] = useState(() => getInitialState().city || '');
  const [postalCode, setPostalCode] = useState(() => getInitialState().postalCode || '');
  const [cloverMerchantId, setCloverMerchantId] = useState(() => getInitialState().cloverMerchantId || '');
  const [subsector, setSubsector] = useState(() => getInitialState().subsector || 'Restaurant');
  const [gstNum1, setGstNum1] = useState(() => getInitialState().gstNum1 || '');
  const [gstNum2, setGstNum2] = useState(() => getInitialState().gstNum2 || '');
  const [qstNum1, setQstNum1] = useState(() => getInitialState().qstNum1 || '');
  const [qstNum2, setQstNum2] = useState(() => getInitialState().qstNum2 || '');
  const [revenueQuebecId, setRevenueQuebecId] = useState(() => getInitialState().revenueQuebecId || '');
  const [billingFileNum, setBillingFileNum] = useState(() => getInitialState().billingFileNum || '');
  const [authCode, setAuthCode] = useState(() => getInitialState().authCode || '');
  const [merchantFirstName, setMerchantFirstName] = useState(() => getInitialState().merchantFirstName || '');
  const [merchantLastName, setMerchantLastName] = useState(() => getInitialState().merchantLastName || '');
  const [mobile, setMobile] = useState(() => getInitialState().mobile || '');
  const [step, setStep] = useState(() => getInitialState().step || 0);
  const [extraDevices, setExtraDevices] = useState(() => getInitialState().extraDevices || 0);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [customerId, setCustomerId] = useState<number | null>(null);

  // Validation states
  const [touched, setTouched] = useState<{[key: string]: boolean}>({});
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [submitted, setSubmitted] = useState(false);
  const [qstValid, setQstValid] = useState(false);
  const [qstLoading, setQstLoading] = useState(false);
  const [qstError, setQstError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Plan selection state
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [planSubmitting, setPlanSubmitting] = useState(false);
  const [planError, setPlanError] = useState('');

  // Helper validation functions
  const isAlphanumeric = (v: string) => /^[a-zA-Z0-9 '\u2019]+$/.test(v);
  const isPhone = (v: string) => /^[0-9\-+() ]{7,20}$/.test(v);
  const isPostal = (v: string) => /^[A-Za-z0-9]{6}$/.test(v);
  const isMerchantId = (v: string) => /^[a-zA-Z0-9]+$/.test(v);
  const isBillingFile = (v: string) => /^[a-zA-Z0-9]{6}$/.test(v);
  const isAuthCode = (v: string) => /^([a-zA-Z0-9]{4}-)*[a-zA-Z0-9]{1,4}$/.test(v);
  const isMobile = (v: string) => /^[0-9]{10}$/.test(v.replace(/\D/g, ''));
  const isMerchantName = (v: string) => v.length > 1 && /^[a-zA-Z .,'-]+$/.test(v);

  // Postal code formatter
  const handlePostalCode = (v: string) => {
    let val = v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    setPostalCode(val);
  };

  // Auth code formatter
  const handleAuthCode = (v: string) => {
    let val = v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (val.length > 8) val = val.slice(0, 8);
    if (val.length > 4) val = val.slice(0, 4) + '-' + val.slice(4);
    setAuthCode(val);
  };

  // QST validation
  const validateQST = async () => {
    setQstLoading(true);
    setQstError('');
    try {
      // First, check if QST number already exists
      const validateQST = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api'}/api/check-tvq/${qstNum1}TQ${qstNum2}`);
      if (!validateQST.ok) {
        throw new Error('Failed to validate QST details');
      }
      const validateQSTResponse = await validateQST.json();
      if (validateQSTResponse && validateQSTResponse.exists) {
        setQstError(t('qstNumberExists'));
        setQstValid(false);
        return;
      }

      // If QST number doesn't exist, proceed with the existing validation
      const payload = {
        mandataryId: qstNum1,
        authorizationCode: authCode,
        QSTNumber: `${qstNum1}TQ${qstNum2}`,
        GSTNumber: `${gstNum1}RT${gstNum2}`,
        billingFileNumber: billingFileNum,
      };
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api'}/api/validate-rq-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!data.rqError) {
        setQstValid(true);
        setRevenueQuebecId(qstNum1); // still assuming this logic is desired
        setQstError('');
      } else {
        setQstError(data.message || 'Invalid QST details');
        setQstValid(false);
      }
    } catch (err: unknown) {
      setQstError((err && typeof err === 'object' && 'message' in err) ? (err as Error).message : 'Failed to validate QST details');
      setQstValid(false);
    } finally {
      setQstLoading(false);
    }
  };

  // Step validation
  const validateStep = () => {
    const newErrors: {[key: string]: string} = {};
    if (step === 0) {
      if (!company) newErrors.company = 'Required';
      else if (!isAlphanumeric(company)) newErrors.company = "Only letters, numbers, spaces, and apostrophe (') allowed.";
      if (!companyTel) newErrors.companyTel = 'Required';
      else if (!isPhone(companyTel)) newErrors.companyTel = 'Invalid phone';
    } else if (step === 1) {
      if (!streetNumber) newErrors.streetNumber = 'Required';
      else if (!isAlphanumeric(streetNumber)) newErrors.streetNumber = 'Alphanumeric only';
      if (!streetName) newErrors.streetName = 'Required';
      else if (!isAlphanumeric(streetName)) newErrors.streetName = 'Alphanumeric only';
      if (!city) newErrors.city = 'Required';
      else if (!isAlphanumeric(city)) newErrors.city = 'Alphanumeric only';
      if (!postalCode) newErrors.postalCode = 'Required';
      else if (!isPostal(postalCode)) newErrors.postalCode = 'Format: A1A 1A1';
    } else if (step === 2) {
      if (!cloverMerchantId) newErrors.cloverMerchantId = 'Required';
      else if (!isMerchantId(cloverMerchantId)) newErrors.cloverMerchantId = 'Alphanumeric only';
      if (!gstNum1 || gstNum1.length !== 9) newErrors.gstNum1 = '9 digits required';
      if (!gstNum2 || gstNum2.length !== 4) newErrors.gstNum2 = '4 digits required';
      if (!qstNum1 || qstNum1.length !== 10) newErrors.qstNum1 = '10 digits required';
      if (!qstNum2 || qstNum2.length !== 4) newErrors.qstNum2 = '4 digits required';
      if (!revenueQuebecId) newErrors.revenueQuebecId = 'Required';
      if (!billingFileNum || !isBillingFile(billingFileNum)) newErrors.billingFileNum = '6 alphanumeric';
      const authCodeRaw = authCode.replace(/-/g, '');
      if (!authCodeRaw || authCodeRaw.length !== 8) newErrors.authCode = '8 alphanumeric chars required';
      if (!merchantFirstName || !isMerchantName(merchantFirstName)) newErrors.merchantFirstName = 'Invalid first name';
      if (!merchantLastName || !isMerchantName(merchantLastName)) newErrors.merchantLastName = 'Invalid last name';
      if (!mobile || !isMobile(mobile)) newErrors.mobile = '10 digits required';
    }
    // Step 3 (Device Configuration) doesn't need validation as it's optional
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateStep()) {
      setStep(step + 1);
    } else {
      // Mark all fields in this step as touched
      setTouched(t => {
        const newTouched = { ...t };
        if (step === 0) {
          newTouched.company = true;
          newTouched.companyTel = true;
        } else if (step === 1) {
          newTouched.streetNumber = true;
          newTouched.streetName = true;
          newTouched.city = true;
          newTouched.postalCode = true;
        } else if (step === 2) {
          newTouched.cloverMerchantId = true;
          newTouched.gstNum1 = true;
          newTouched.gstNum2 = true;
          newTouched.qstNum1 = true;
          newTouched.qstNum2 = true;
          newTouched.revenueQuebecId = true;
          newTouched.billingFileNum = true;
          newTouched.authCode = true;
          newTouched.merchantFirstName = true;
          newTouched.merchantLastName = true;
          newTouched.mobile = true;
        }
        return newTouched;
      });
    }
  };

  const handleBack = () => setStep(step - 1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    if (!qstValid) {
      setSubmitError('Please validate QST details before submitting.');
      return;
    }
    if (validateStep()) {
      setSubmitting(true);
      try {
        const formData = {
          companyName: company,
          mobile: mobile,
          streetNumber: streetNumber,
          streetName: streetName,
          city: city,
          postalCode: postalCode,
          customerName: [merchantFirstName.trim(), merchantLastName.trim()].filter(Boolean).join(' '),
          email: email,
          phone: companyTel,
          fileNumber: billingFileNum,
          TPS: `${gstNum1}RT${gstNum2}`,
          merchantId: qstNum1,
          activationCode: authCode,
          TVQ: `${qstNum1}TQ${qstNum2}`,
          cloverMID: cloverMerchantId
        };
        const result = await onSubmitOnboarding(formData);
        if (result.success && result.customerId) {
          setCustomerId(result.customerId);
          // Persist customerId to session storage
          sessionStorage.setItem('onboardingCustomerId', result.customerId.toString());
          setFormSubmitted(true);
          setStep(3);
        } else {
          throw new Error(result.message || 'Failed to submit. Please try again.');
        }
      } catch (err: unknown) {
        setSubmitError((err && typeof err === 'object' && 'message' in err) ? (err as Error).message : 'Submission failed.');
      } finally {
        setSubmitting(false);
      }
    } else {
      setTouched(t => {
        const newTouched = { ...t };
        Object.keys(errors).forEach(k => newTouched[k] = true);
        return newTouched;
      });
    }
  };

  const handleDeviceSubmit = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent any form submission
    // Set submitted state to true
    setSubmitted(true);
    // Call onComplete callback if provided
    if (typeof onComplete === 'function') {
      onComplete();
    }
    // Navigate to the plan selection page, passing the customerId
    navigate('/choose-your-plan', { replace: true, state: { customerId } });
  };

  // Save to localStorage on any change
  useEffect(() => {
    if (!submitted) { // Only save if not submitted
      const data = {
        company, companyTel, streetNumber, streetName, city, postalCode, cloverMerchantId, subsector,
        gstNum1, gstNum2, qstNum1, qstNum2, revenueQuebecId, billingFileNum, authCode, merchantFirstName, merchantLastName, mobile, step, email, extraDevices
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
    }
  }, [company, companyTel, streetNumber, streetName, city, postalCode, cloverMerchantId, subsector, gstNum1, gstNum2, qstNum1, qstNum2, revenueQuebecId, billingFileNum, authCode, merchantFirstName, merchantLastName, mobile, step, email, extraDevices, submitted]);

  // Effect to handle final submission
  useEffect(() => {
    if (submitted) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  }, [submitted, navigate]);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Add this computed variable before the return statement
  const canValidateQST =
    gstNum1.length === 9 &&
    gstNum2.length === 4 &&
    qstNum1.length === 10 &&
    qstNum2.length === 4 &&
    authCode.replace(/-/g, '').length === 8 &&
    billingFileNum.length === 6;

  if (submitted) {
    return <div style={{textAlign: 'center', fontWeight: 600, fontSize: '1.2rem'}}>Thank you! Your details have been submitted.</div>;
  }

  return (
    <div className="user-details-card fade-in">
      <form onSubmit={step === 2 ? handleSubmit : handleNext} 
        className={step === 2 ? 'wide-form' : ''}
        style={{maxWidth: 700, margin: '0 auto', textAlign: 'left'}} 
        autoComplete="off">
        <div className="form-stepper">
          {getSteps(t).map((label, idx) => (
            <div key={label} className={`step${idx <= step ? ' active' : ''}${idx === step ? ' current' : ''}`}> 
              <div className="step-content">
                <div className="step-circle">{idx + 1}</div>
                <div className="step-label">{label}</div>
                <div className="step-bar" />
              </div>
            </div>
          ))}
        </div>
        {step === 0 && (
          <>
            <h2 className="form-title">{t('companyInfo')}</h2>
            <div className="form-divider" />
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={email} disabled className="form-input disabled" />
            </div>
            <div className="form-group">
              <label htmlFor="company">{t('companyName')} <span className="required">*</span></label>
              <input id="company" type="text" value={company} onChange={e => setCompany(e.target.value)} onBlur={() => setTouched(t => ({...t, company: true}))} className={`form-input${errors.company && touched.company ? ' error' : ''}`} required />
              {touched.company && errors.company && <div className="form-error">{errors.company}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="companyTel">Company Telephone <span className="required">*</span></label>
              <input id="companyTel" type="tel" value={companyTel} onChange={e => setCompanyTel(e.target.value)} onBlur={() => setTouched(t => ({...t, companyTel: true}))} className={`form-input${errors.companyTel && touched.companyTel ? ' error' : ''}`} required />
              {touched.companyTel && errors.companyTel && <div className="form-error">{errors.companyTel}</div>}
            </div>
          </>
        )}
        {step === 1 && (
          <>
            <h2 className="form-title">{t('address')}</h2>
            <div className="form-divider" />
            <div className="form-row">
              <div className="form-group half">
                <label htmlFor="streetNumber">{t('streetNumber')} <span className="required">*</span></label>
                <input id="streetNumber" type="text" value={streetNumber} onChange={e => setStreetNumber(e.target.value)} onBlur={() => setTouched(t => ({...t, streetNumber: true}))} className={`form-input${errors.streetNumber && touched.streetNumber ? ' error' : ''}`} required />
                {touched.streetNumber && errors.streetNumber && <div className="form-error">{errors.streetNumber}</div>}
              </div>
              <div className="form-group half">
                <label htmlFor="streetName">{t('streetName')} <span className="required">*</span></label>
                <input id="streetName" type="text" value={streetName} onChange={e => setStreetName(e.target.value)} onBlur={() => setTouched(t => ({...t, streetName: true}))} className={`form-input${errors.streetName && touched.streetName ? ' error' : ''}`} required />
                {touched.streetName && errors.streetName && <div className="form-error">{errors.streetName}</div>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group half">
                <label htmlFor="city">{t('city')} <span className="required">*</span></label>
                <input id="city" type="text" value={city} onChange={e => setCity(e.target.value)} onBlur={() => setTouched(t => ({...t, city: true}))} className={`form-input${errors.city && touched.city ? ' error' : ''}`} required />
                {touched.city && errors.city && <div className="form-error">{errors.city}</div>}
              </div>
              <div className="form-group half">
                <label htmlFor="postalCode">{t('postalCode')} <span className="required">*</span></label>
                <input id="postalCode" type="text" value={postalCode} onChange={e => handlePostalCode(e.target.value)} onBlur={() => setTouched(t => ({...t, postalCode: true}))} className={`form-input${errors.postalCode && touched.postalCode ? ' error' : ''}`} maxLength={6} required />
                {touched.postalCode && errors.postalCode && <div className="form-error">{errors.postalCode}</div>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group half">
                <label htmlFor="province">{t('province')}</label>
                <input id="province" type="text" value="Quebec (CA)" disabled className="form-input disabled" />
              </div>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <h2 className="form-title">{t('taxMerchantInfo')}</h2>
            <div className="form-divider" />
            <div className="form-group">
              <label htmlFor="cloverMerchantId">{t('cloverMerchantId')} <span className="required">*</span></label>
              <input id="cloverMerchantId" type="text" value={cloverMerchantId} onChange={e => setCloverMerchantId(e.target.value)} onBlur={() => setTouched(t => ({...t, cloverMerchantId: true}))} className={`form-input${errors.cloverMerchantId && touched.cloverMerchantId ? ' error' : ''}`} required />
              {touched.cloverMerchantId && errors.cloverMerchantId && <div className="form-error">{errors.cloverMerchantId}</div>}
            </div>
            <div className="form-group">
              <label>{t('subsector')} <span className="required">*</span></label>
              <div className="form-radio-group">
                {subsectorOptions.map(opt => (
                  <label key={opt.value} className="form-radio-label">
                    <input type="radio" name="subsector" value={opt.value} checked={subsector === opt.value} onChange={() => setSubsector(opt.value)} required /> {t(opt.value as keyof typeof import('../locales/en-US.json'))}
                  </label>
                ))}
              </div>
              {touched.subsector && !subsector && <div className="form-error">{t('required')}</div>}
            </div>
            <div className="form-group gst-group">
              <label htmlFor="gstNum1">
                GST Number <span className="required">*</span>
                <span className="info-icon" data-tooltip={t('gstInfo')}>i</span>
              </label>
              <div className="gst-inputs">
                <input
                  type="text"
                  id="gstNum1"
                  value={gstNum1}
                  onChange={e => setGstNum1(e.target.value)}
                  maxLength={9}
                  className="form-input gst-part"
                  required
                  onBlur={() => setTouched(t => ({...t, gstNum1: true}))}
                />
                <span className="gst-highlight">RT</span>
                <input
                  type="text"
                  id="gstNum2"
                  value={gstNum2}
                  onChange={e => setGstNum2(e.target.value)}
                  maxLength={4}
                  className="form-input gst-part"
                  required
                  onBlur={() => setTouched(t => ({...t, gstNum2: true}))}
                />
              </div>
              {touched.gstNum1 && errors.gstNum1 && <div className="form-error">{errors.gstNum1}</div>}
              {touched.gstNum2 && errors.gstNum2 && <div className="form-error">{errors.gstNum2}</div>}
            </div>
            <div className="form-group qst-group">
              <label htmlFor="qstNum1">
                QST Number <span className="required">*</span>
                <span className="info-icon" data-tooltip={t('qstInfo')}>i</span>
              </label>
              <div className="qst-inputs">
                <input
                  type="text"
                  id="qstNum1"
                  value={qstNum1}
                  onChange={e => setQstNum1(e.target.value)}
                  maxLength={10}
                  className="form-input qst-part"
                  required
                  onBlur={() => setTouched(t => ({...t, qstNum1: true}))}
                />
                <span className="qst-highlight">TQ</span>
                <input
                  type="text"
                  id="qstNum2"
                  value={qstNum2}
                  onChange={e => setQstNum2(e.target.value)}
                  maxLength={4}
                  className="form-input qst-part"
                  required
                  onBlur={() => setTouched(t => ({...t, qstNum2: true}))}
                />
              </div>
              {touched.qstNum1 && errors.qstNum1 && <div className="form-error">{errors.qstNum1}</div>}
              {touched.qstNum2 && errors.qstNum2 && <div className="form-error">{errors.qstNum2}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="authCode">
                {t('authCode')} <span className="required">*</span>
                <span className="info-icon" data-tooltip={t('authCodeInfo')}>i</span>
              </label>
              <input id="authCode" type="text" value={authCode} onChange={e => handleAuthCode(e.target.value)} onBlur={() => setTouched(t => ({...t, authCode: true}))} className={`form-input${errors.authCode && touched.authCode ? ' error' : ''}`} maxLength={9} required placeholder="XXXX-XXXX" />
              {touched.authCode && errors.authCode && <div className="form-error">{errors.authCode}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="billingFileNum">
                {t('billingFileNum')} <span className="required">*</span>
                <span className="info-icon" data-tooltip={t('billingFileInfo')}>i</span>
              </label>
              <input id="billingFileNum" type="text" value={billingFileNum} onChange={e => setBillingFileNum(e.target.value)} onBlur={() => setTouched(t => ({...t, billingFileNum: true}))} className={`form-input${errors.billingFileNum && touched.billingFileNum ? ' error' : ''}`} maxLength={6} required />
              {touched.billingFileNum && errors.billingFileNum && <div className="form-error">{errors.billingFileNum}</div>}
            </div>
            <div className="form-row">
              <button
                type="button"
                onClick={validateQST}
                disabled={qstLoading || !canValidateQST}
                className="form-btn-inline full-width"
              >
                {qstLoading ? t('validating') : t('validateQST')}
              </button>
            </div>
            {qstError && <div className="form-error">{qstError}</div>}
            {!canValidateQST && (
              <div className="qst-helper" style={{ color: 'red', marginTop: 8 }}>
                {t('qstValidationHelper')}<br />
                <ul style={{margin: '4px 0 0 18px', padding: 0, color: 'red', fontSize: '0.98em'}}>
                  {gstNum1.length !== 9 && <li>{t('qstFieldGSTNumber')}</li>}
                  {gstNum2.length !== 4 && <li>{t('qstFieldGSTNumberSuffix')}</li>}
                  {qstNum1.length !== 10 && <li>{t('qstFieldQSTNumber')}</li>}
                  {qstNum2.length !== 4 && <li>{t('qstFieldQSTNumberSuffix')}</li>}
                  {authCode.replace(/-/g, '').length !== 8 && <li>{t('qstFieldAuthCode')}</li>}
                  {billingFileNum.length !== 6 && <li>{t('qstFieldBillingFile')}</li>}
                </ul>
              </div>
            )}
            {qstValid ? (
              <div className="qst-success" style={{ color: 'green', marginTop: 8, fontWeight: 500 }}>
                ✓ QST validated successfully!
              </div>
            ) : (
              <div className="qst-helper" style={{ color: '#888', marginTop: 8 }}>
                {t('qstValidationPrompt')}
              </div>
            )}
            <div className="form-row">
              <div className="form-group half">
                <label htmlFor="merchantFirstName">{t('merchantFirstName')} <span className="required">*</span></label>
                <input
                  id="merchantFirstName"
                  type="text"
                  value={merchantFirstName}
                  onChange={e => setMerchantFirstName(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, merchantFirstName: true }))}
                  className={`form-input${errors.merchantFirstName && touched.merchantFirstName ? ' error' : ''}`}
                  required
                />
                {touched.merchantFirstName && errors.merchantFirstName && <div className="form-error">{errors.merchantFirstName}</div>}
              </div>
              <div className="form-group half">
                <label htmlFor="merchantLastName">{t('merchantLastName')} <span className="required">*</span></label>
                <input
                  id="merchantLastName"
                  type="text"
                  value={merchantLastName}
                  onChange={e => setMerchantLastName(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, merchantLastName: true }))}
                  className={`form-input${errors.merchantLastName && touched.merchantLastName ? ' error' : ''}`}
                  required
                />
                {touched.merchantLastName && errors.merchantLastName && <div className="form-error">{errors.merchantLastName}</div>}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="mobile">{t('mobile')} <span className="required">*</span></label>
              <input
                id="mobile"
                type="tel"
                value={mobile}
                onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                onBlur={() => setTouched(t => ({ ...t, mobile: true }))}
                className={`form-input${errors.mobile && touched.mobile ? ' error' : ''}`}
                maxLength={10}
                required
              />
              {touched.mobile && errors.mobile && <div className="form-error">{errors.mobile}</div>}
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <h2 className="form-title">{t('deviceConfiguration')}</h2>
            <div className="form-divider" />
            <div className="device-config-message">
              <div className="message-box">
                <h3>{t('yourDefaultPlan')}</h3>
                <p>{t('yourDefaultPlanMessage1')} <strong>5</strong> {t('yourDefaultPlanMessage2')}</p>
                <p>{t('wouldYouLikeToAddMoreDevices')}</p>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="extraDevices">{t('additionalDevices')}</label>
              <div className="device-input-group">
                <button 
                  type="button" 
                  className="device-btn" 
                  onClick={() => setExtraDevices(Math.max(0, extraDevices - 1))}
                  disabled={extraDevices === 0}
                >
                  -
                </button>
                <input 
                  id="extraDevices" 
                  type="number" 
                  value={extraDevices} 
                  onChange={e => setExtraDevices(Math.max(0, parseInt(e.target.value) || 0))}
                  className="form-input device-input" 
                  min="0"
                  max="20"
                />
                <button 
                  type="button" 
                  className="device-btn" 
                  onClick={() => setExtraDevices(extraDevices + 1)}
                  disabled={extraDevices >= 20}
                >
                  +
                </button>
              </div>
              <div className="device-summary">
                <p><strong>{t('totalDevices')}:</strong> {5 + extraDevices} <span style={{color:'#888', fontSize:'0.95em'}}>(5 free)</span></p>
                {extraDevices > 0 && (
                  <p className="device-note">{t('additionalDevicesNote')}</p>
                )}
              </div>
            </div>
            {planError && <div className="form-error" style={{textAlign: 'center', marginTop: '1rem'}}>{planError}</div>}
          </>
        )}
        <div className="form-wizard-nav">
          <div className="form-wizard-nav-left">
            {step > 0 && step < 3 && (
              <button type="button" className="form-btn-inline" onClick={handleBack} disabled={submitting}>
                {t('back')}
              </button>
            )}
          </div>
          <div className="form-wizard-nav-right">
            {step < 2 && (
              <button type="submit" className="form-btn-inline" disabled={submitting}>
                {t('next')}
              </button>
            )}
            {step === 2 && (
              <button
                type="submit"
                className="form-submit-btn"
                disabled={submitting || !qstValid}
                title={!qstValid ? 'Please validate QST before submitting' : ''}
              >
                {submitting ? 'Submitting...' : !qstValid ? 'Validate QST to Submit' : 'Submit'}
              </button>
            )}
            {step === 3 && (
              <button 
                type="button" 
                onClick={handleDeviceSubmit}
                className="form-submit-btn"
              >
                {t('continueToPlanSelection')}
              </button>
            )}
          </div>
        </div>
        {submitError && <div className="form-error" style={{textAlign: 'center', marginTop: '1rem'}}>{submitError}</div>}
      </form>
    </div>
  );
};

export default UserDetailsForm; 